import { Component, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { AnswerHintKeys, LifemapQuestion } from '@shared/models/step.model';
import { AnswerValue } from '@shared/services/lifemap/lifemap.service';
import { NgOnInitWriteBugAbstractComponent } from '@shared/abstract/ngOnInitWriteBug.abstract.component';
import { ControlValueAccessor, ValidationErrors } from '@angular/forms';

export interface LifemapAnswer {
  title: string;
  answerValue: AnswerValue;
}

@Component({
  selector: 'pl-single-question',
  templateUrl: './singleQuestion.component.html',
  styleUrls: ['./singleQuestion.component.scss'],
})
export class SingleQuestionComponent
  extends NgOnInitWriteBugAbstractComponent<AnswerValue>
  implements OnInit, ControlValueAccessor
{
  @Input({ required: true }) public question!: LifemapQuestion;
  @Input({ required: true }) public questionNumber!: number;
  @Input({ required: true }) public totalNumberQuestions!: number;

  public questionNumberTitle: WritableSignal<string> = signal('');
  public answerValue: WritableSignal<AnswerValue> = signal(null);
  public lifemapAnswers: WritableSignal<LifemapAnswer[]> = signal([]);

  public plOnInit(): void {
    this.questionNumberTitle.set(`Q${this.questionNumber}/${this.totalNumberQuestions}:`);
    this.lifemapAnswers.set(this.getLifemapAnswersFromQuestion(this.question));

    if (this.controlDir.control) {
      this.controlDir.control.setValidators([this.validate.bind(this)]);
      this.controlDir.control.updateValueAndValidity();
    }
  }

  private validate(): ValidationErrors | null {
    if (this.answerValue() === null) {
      return {
        required: true,
      };
    }

    return null;
  }

  public plWriteValue(valueToWrite: AnswerValue): void {
    this.answerValue.set(valueToWrite);
    this.controlDir.control?.updateValueAndValidity();
  }

  private getLifemapAnswersFromQuestion(question: LifemapQuestion): LifemapAnswer[] {
    return Object.entries(question.answerHint)
      .filter(([, answerTitle]: [string, string | null]): boolean => !!answerTitle)
      .map(([value, answerTitle]: [string, string | null]): LifemapAnswer => {
        if (answerTitle) {
          return {
            title: answerTitle,
            answerValue: this.getAnswerValueFromHint(value as AnswerHintKeys),
          };
        } else {
          throw new Error(`null title should have been filtered out.`);
        }
      });
  }

  private getAnswerValueFromHint(answerKeyHint: AnswerHintKeys): AnswerValue {
    switch (answerKeyHint) {
      case '1':
        return 1;
      case '2':
        return 2;
      case '3':
        return 3;
      case '4':
        return 4;
      case '5':
        return 5;
      case 'N/A':
        return 6;
      default:
        throw new Error(`Unknown value for answerKeyHint: ${answerKeyHint}`);
    }
  }

  public onQuestionAnswered(answerValue: AnswerValue): void {
    this.answerValue.set(answerValue);
    this.emitChanges(answerValue);
  }

  private emitChanges(valueToEmit: AnswerValue): void {
    this.onTouchedCallback();
    this.onChangeCallback(valueToEmit);
  }
}
